import { defineStore } from 'pinia';
import { ref } from 'vue';
import Cookies from 'js-cookie';
import type {
    ClusterResult,
    ClusterMap2DInfoItem,
    ClusterMap3DInfoItem,
    ClusterMapBaseInfoIn2DMapData,
    ClusterMapBaseInfoItem,
} from '@common/cluster-common';
import {
    fetchClusterResult,
    fetchClusterShareResult,
    fetchCluster3DMapInfo,
    fetchCluster2DMapInfo,
    fetchTestClusterResult,
} from '@common/cluster-common';

interface UserData {
    id: string;
    answer: string | number | null;
}

interface UserSelectPictureData {
    id: string;
    answer: number[] | null;
}

const useClusterInAppStore = defineStore('clusterInApp', () => {
    const isReadyInAppWebView = ref(false);
    const tokenValue = ref('');
    const declareDateTimeValue = ref('');
    const shareToken = ref('');
    const userData = ref<UserData[]>([]);
    const userSelectPictureData = ref<UserSelectPictureData[]>([]);
    const clusterResultData = ref<ClusterResult | null>(null);
    const map2DDefaultData = ref<ClusterMap2DInfoItem[]>([]);
    const map3DDefaultData = ref<ClusterMap3DInfoItem[]>([]);
    const selectedCluster3DMapInfo = ref<ClusterMapBaseInfoItem | null>(null);
    const selectedCluster2DMapInfo = ref<ClusterMapBaseInfoIn2DMapData | null>(null);

    const setInitData = () => {
        tokenValue.value = Cookies.get('tokenValue') ?? '';
        declareDateTimeValue.value = Cookies.get('declareDateTimeValue') ?? '';
    };

    const setTokenInfo = (code: string, declareTime: string): Promise<void> => {
        return new Promise(resolve => {
            shareToken.value = code;
            declareDateTimeValue.value = declareTime;
            resolve();
        });
    };

    const setRemoveResultData = () => {
        clusterResultData.value = null;
        map2DDefaultData.value = [];
        map3DDefaultData.value = [];
        selectedCluster3DMapInfo.value = null;
        selectedCluster2DMapInfo.value = null;
        shareToken.value = '';
        declareDateTimeValue.value = '';
    };

    const getInAppClusterResult = async () => {
        const resp = await fetchClusterResult(declareDateTimeValue.value);
        if (resp.IsSuccess) {
            setRemoveResultData();
            shareToken.value = resp.Data.ShareToken;
            declareDateTimeValue.value = resp.Data.ClusterDeclareTime;
            clusterResultData.value = resp.Data;
            map2DDefaultData.value = resp.Data.Cluster2DMapInfoList;
            map3DDefaultData.value = resp.Data.Cluster3DMapBasicInfoList;
            selectedCluster3DMapInfo.value = resp.Data.CurrentCluster3DMapInfo;
            selectedCluster2DMapInfo.value = resp.Data.CurrentBodyTypeInfoIn2DMap;
        }
        return resp;
    };

    const getInAppShareResult = async () => {
        const resp = await fetchClusterShareResult(shareToken.value, declareDateTimeValue.value);
        if (resp.IsSuccess) {
            setRemoveResultData();
            shareToken.value = resp.Data.ShareToken;
            declareDateTimeValue.value = resp.Data.ClusterDeclareTime;
            clusterResultData.value = resp.Data;
            map2DDefaultData.value = resp.Data.Cluster2DMapInfoList;
            map3DDefaultData.value = resp.Data.Cluster3DMapBasicInfoList;
            selectedCluster3DMapInfo.value = resp.Data.CurrentCluster3DMapInfo;
            selectedCluster2DMapInfo.value = resp.Data.CurrentBodyTypeInfoIn2DMap;
        }
        return resp;
    };

    const getCluster3DMapInfo = async (clusterId: string) => {
        const resp = await fetchCluster3DMapInfo(clusterId, declareDateTimeValue.value, shareToken.value);
        if (resp.IsSuccess) {
            selectedCluster3DMapInfo.value = resp.Data;
        }
        return resp;
    };

    const getCluster2DMapInfo = async (bodyTypeId: string) => {
        const resp = await fetchCluster2DMapInfo(bodyTypeId, declareDateTimeValue.value, shareToken.value);
        if (resp.IsSuccess) {
            selectedCluster2DMapInfo.value = resp.Data;
        }
        return resp;
    };

    const getClusterTestInAppViewResult = async (payload: {
        shareToken: string;
        currentClusterID: string;
        previousClusterID: string;
    }) => {
        const resp = await fetchTestClusterResult(payload);
        if (resp.IsSuccess) {
            setRemoveResultData();
            shareToken.value = resp.Data.ShareToken;
            declareDateTimeValue.value = resp.Data.ClusterDeclareTime;
            clusterResultData.value = resp.Data;
            map2DDefaultData.value = resp.Data.Cluster2DMapInfoList;
            map3DDefaultData.value = resp.Data.Cluster3DMapBasicInfoList;
            selectedCluster3DMapInfo.value = resp.Data.CurrentCluster3DMapInfo;
            selectedCluster2DMapInfo.value = resp.Data.CurrentBodyTypeInfoIn2DMap;
        }
        return resp;
    };

    setInitData();

    return {
        isReadyInAppWebView,
        tokenValue,
        declareDateTimeValue,
        shareToken,
        userData,
        userSelectPictureData,
        clusterResultData,
        map2DDefaultData,
        map3DDefaultData,
        selectedCluster3DMapInfo,
        selectedCluster2DMapInfo,
        setInitData,
        setTokenInfo,
        setRemoveResultData,
        getInAppClusterResult,
        getInAppShareResult,
        getCluster3DMapInfo,
        getCluster2DMapInfo,
        getClusterTestInAppViewResult,
    };
});

export default useClusterInAppStore;
